import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import {
  Business,
  School,
  Assignment,
  CheckCircle,
  Description,
  Feedback,
  Work,
  Schedule,
  BarChart,
} from '@mui/icons-material';

const Placements = () => {
  const companyProcedure = [
    {
      icon: <Assignment />,
      text: 'The company fills in the Interest Form available on college website and submits it online. The Interest Form includes company domain, contact person details, company website and its nature.'
    },
    {
      icon: <Description />,
      text: 'If found suitable, TPO cell sends brochure and placement procedure details to the company.'
    },
    {
      icon: <Assignment />,
      text: 'The company then fills in detailed registration form including information related to job profile, location, CTC, eligibility criteria and selection round.'
    },
    {
      icon: <CheckCircle />,
      text: 'TPO cell sends shortlisted students\' details to the company as per the eligibility criteria.'
    },
    {
      icon: <CheckCircle />,
      text: 'The company can shortlist them before beginning of the placement process.'
    },
    {
      icon: <Schedule />,
      text: 'The company is allotted slots and dates for conducting Pre-Placement Talk (PPT)/Written Test/Online Test/ Group Discussion (GD)/Interview with a request to confirm the same by a specified date.'
    },
    {
      icon: <Description />,
      text: 'The companies are advised to conduct the PPT much before the final placement commences. This will provide ample time to the students for making a considered decision in joining the company.'
    },
    {
      icon: <Work />,
      text: 'The company conducts the selection rounds and confirms the list of selected students.'
    },
    {
      icon: <Feedback />,
      text: 'Company submits an online feedback form after the completion of the placement process.'
    },
    {
      icon: <Work />,
      text: 'The company may also, if interested, conduct selections for summer internship during their visit.'
    },
    {
      icon: <Description />,
      text: 'The company is required to send offer letters to TPO cell, who will then hand it over to the concerned students.'
    }
  ];

  const studentProcedure = [
    {
      icon: <Description />,
      text: 'The relevant information of the company is electronically broadcasted to all the students along with the other additional information furnished by the company.'
    },
    {
      icon: <Assignment />,
      text: 'Students fill in detail form which includes basic details along with information related to academic performance, technical and soft skills. Students need to verify these details.'
    },
    {
      icon: <CheckCircle />,
      text: 'TPO cell does screening to shortlist the eligible students.'
    },
    {
      icon: <Feedback />,
      text: 'Students submit online feedback form after the completion of the placement process.'
    },
    {
      icon: <Work />,
      text: 'The Institute follows "One Student, One Job" policy. Low paid jobs are not counted towards this.'
    },
    {
      icon: <Work />,
      text: 'Job opportunities from Government/PSUs will also be made available to all the students who are not already placed in a PSU.'
    },
    {
      icon: <Work />,
      text: 'Once a student is offered a job in the PSUs/Government, he/she will not be allowed to sit for any further campus recruitment drive.'
    },
    {
      icon: <Work />,
      text: 'As an exception to the "One Student, One Job" policy, a student who has secured a job can appear for another company\'s placement procedure if allowed by the Professor Incharge, Training and Placement Cell.'
    }
  ];

  const handleRedirect = () => {
    window.open('https://www.mbm.ac.in/tpo-mbm-statistics', '_blank');
  };

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 },
      background: '#fff',
      color: '#1F2937',
      minHeight: '100vh'
    }}>
      <Container maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom sx={{ 
          mb: 2,
          fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.5rem' },
          position: 'relative',
          color: '#1F2937',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            background: '#4B5563',
            borderRadius: 2
          }
        }}>
          On-Campus Placement Procedure
        </Typography>

        <Typography variant="h6" align="center" sx={{ 
          mb: 8,
          color: '#374151',
          maxWidth: 800,
          mx: 'auto'
        }}>
          Comprehensive guidelines for companies and students participating in our on-campus recruitment process
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 4,
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  gap: 2
                }}>
                  <Business sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700,
                    color: 'primary.main'
                  }}>
                    For Companies
                  </Typography>
                </Box>

                <List>
                  {companyProcedure.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            color: 'text.secondary',
                            variant: 'body2'
                          }}
                        />
                      </ListItem>
                      {index < companyProcedure.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 4,
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  gap: 2
                }}>
                  <School sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700,
                    color: 'primary.main'
                  }}>
                    For Students
                  </Typography>
                </Box>

                <List>
                  {studentProcedure.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            color: 'text.secondary',
                            variant: 'body2'
                          }}
                        />
                      </ListItem>
                      {index < studentProcedure.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  gap: 3
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2
                  }}>
                    <BarChart sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: 'primary.main'
                    }}>
                      Placement Statistics
                    </Typography>
                  </Box>

                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
                    View detailed placement statistics for CSE, IT, and AI&DS departments including year-wise records, company-wise data, and overall placement trends.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleRedirect}
                    startIcon={<BarChart />}
                    sx={{
                      mt: 2,
                      px: 4,
                      py: 1.5,
                      background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #0d47a1 30%, #1a237e 90%)',
                      }
                    }}
                  >
                    View Placement Statistics
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Placements; 